import { debugTracking } from '$lib/debug';
import { getConsentStatus, isTrackingAllowed } from '$lib/global/consent/consentGate';
import { shouldTrack } from '$lib/global/tracking/helper';

import omit from 'lodash/omit';

import { deferToIntegrations } from './deferredIntegrationEvents';
import { dispatchIdentify, dispatchPage, dispatchTrack } from './firstPartyDispatch';
import { integrationsWithIdsInEvents, integrationsWithOutIdsInEvents } from './integrations';
import { getTrackingService } from './TrackingServiceInstance';

export const trackQuantityEvent = (event: string, properties?: Record<string, unknown>) => {
    track(event, properties, {
        integrations: integrationsWithOutIdsInEvents,
    });
};

export const trackUniqueEvent = (event: string, properties: Record<string, unknown>) => {
    track(event, properties, {
        integrations: integrationsWithIdsInEvents,
    });
};

/**
 * A visitor without consent is measured, but only by us.
 *
 * The event still goes out — straight to the funnel's own origin, skipping analytics.js and
 * therefore every third-party integration. See `firstPartyDispatch`.
 */
const isFirstPartyOnly = (): boolean => !isTrackingAllowed();

/**
 * Whether the Perspective integration would have received this event.
 *
 * Segment applies the per-integration overrides for us on the consented path, and
 * `trackUniqueEvent` uses that to disable `Perspective` explicitly: the `<Event>: <id>`
 * variants exist to give the third-party pixels a per-id event name, and our own backend has
 * never accepted them — it answers 400. `firstPartyDispatch` posts straight to that backend,
 * so it has to apply the same routing itself. Changing the transport must not change which
 * destinations an event reaches.
 *
 * Absent means enabled, matching Segment's treatment of a destination the caller did not
 * mention.
 */
const goesToPerspective = (override: { integrations?: Record<string, boolean> }): boolean =>
    override.integrations?.Perspective !== false;

/**
 * Is the visitor still looking at the banner?
 *
 * CAP-4405: only what happens in that window is staged for the integrations. Once the
 * visitor has answered, either the integrations are live (granted) or they never will be
 * for this visit (denied) — and back-filling a rejected visit on a later opt-in is not
 * something a rejection should leave open.
 */
const isAwaitingDecision = (): boolean => getConsentStatus() === 'pending';

/**
 * Perspective's own analytics already received the event on the first-party path, so a
 * replay of it must reach the third-party integrations only.
 */
const withoutPerspective = (integrations: Record<string, boolean> = {}) => ({
    ...integrations,
    Perspective: false,
});

const sendTrack = (
    event: string,
    properties: Record<string, unknown> | undefined,
    override: { integrations?: Record<string, boolean> },
    context: Record<string, unknown>,
) => {
    window.analytics?.track(event, properties, {
        ...override,
        context,
    });
};

const sendPage = (
    pageName: string,
    properties: Record<string, unknown>,
    integrations: Record<string, boolean>,
    context: Record<string, unknown>,
) => {
    window.analytics?.page(null, pageName, properties, {
        context,
        integrations,
    });
};

export const track = (
    event: string,
    properties: Record<string, unknown> | undefined,
    override: { integrations?: Record<string, boolean> } = {},
) => {
    debugTracking('-> V4 track request:', !!window.analytics, event, properties, override);

    if (!shouldTrack()) {
        return;
    }

    const { context } = generateContext();

    if (isFirstPartyOnly()) {
        if (goesToPerspective(override)) {
            dispatchTrack(event, properties, context);
        }

        // CAP-4405: the `<Event>: <id>` variants are the ones the pixels consume, and they
        // carry `Perspective: false` — on this path they would otherwise reach no
        // destination at all and be lost for good.
        if (isAwaitingDecision()) {
            deferToIntegrations(() =>
                sendTrack(
                    event,
                    properties,
                    { integrations: withoutPerspective(override.integrations) },
                    context,
                ),
            );
        }

        return;
    }

    sendTrack(event, properties, override, context);
};

export const page = (pageName: string, properties: Record<string, unknown>) => {
    debugTracking('-> V4 page request:', !!window.analytics, pageName, properties);

    if (!shouldTrack()) {
        return;
    }

    const { context } = generateContext();

    if (isFirstPartyOnly()) {
        dispatchPage(pageName, properties, context);

        if (isAwaitingDecision()) {
            deferToIntegrations(() =>
                sendPage(
                    pageName,
                    properties,
                    withoutPerspective(integrationsWithOutIdsInEvents),
                    context,
                ),
            );
        }

        return;
    }

    sendPage(pageName, properties, integrationsWithOutIdsInEvents, context);
};

export const identify = (traits?: Record<string, unknown>) => {
    debugTracking('-> V4 identify request:', !!window.analytics, traits);

    if (!shouldTrack()) {
        return;
    }

    const eventContext = generateContext();

    if (isFirstPartyOnly()) {
        dispatchIdentify(traits, eventContext.context);

        if (isAwaitingDecision()) {
            // Pin the id now: the replay runs after consent moved the visitor id from
            // sessionStorage to localStorage, and the traits belong to the visitor as they
            // were identified, not to whoever the service resolves later.
            const { userId } = getTrackingService();

            deferToIntegrations(
                () =>
                    window.analytics?.identify(userId, traits, {
                        ...eventContext,
                        integrations: withoutPerspective(),
                    }),
            );
        }

        return;
    }

    window.analytics?.identify(getTrackingService().userId, traits, eventContext);
};

/**
 * The `context.page` block Segment's analytics.js sets from `window.location` on every call.
 *
 * The first-party dispatch has to include it too: the metrics ingest
 * (`perspective-metrics`, `poc/schemas.baseActivitySchema`) requires `context.page.search`
 * and rejects the whole activity without it — which is why a first-party event returned 200
 * but never produced a metrics row. `context.page.search` is also where that service parses
 * UTM attribution from. On the consented path Segment overwrites this with the same values.
 */
const pageContext = () => ({
    path: window.location.pathname,
    referrer: document.referrer,
    search: window.location.search,
    title: document.title,
    url: window.location.href,
});

const generateContext = () => {
    const pipeline = getTrackingService().getEventContext();

    return {
        context: {
            perspective: omit(pipeline, 'trackingTitles'),
            dictionary: pipeline.trackingTitles,
            page: pageContext(),
            userAgent: window.navigator.userAgent,
            locale: window.navigator.language,
        },
    };
};

export default {
    track,
    page,
    identify,
};
